/**
 * System Service Hooks
 * React hooks for accessing system services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all system-related services
 * @returns SystemServices object containing all system service instances
 */
export function useSystemServices() {
    const { services } = useCCMS();
    if (!services?.system) {
        throw new Error('System services not available. Ensure CCMSProvider is configured with system services.');
    }
    return services.system;
}
/**
 * Hook to access SystemService
 */
export function useSystemService() {
    return useSystemServices().system;
}
/**
 * Hook to access ApiRequestLogService
 */
export function useApiRequestLogService() {
    return useSystemServices().apiRequestLog;
}
/**
 * Hook to access QueryPerformanceLogService
 */
export function useQueryPerformanceLogService() {
    return useSystemServices().queryPerformanceLog;
}
/**
 * Hook to access LogsService
 */
export function useLogsService() {
    return useSystemServices().logs;
}
/**
 * Hook to access HealthService
 */
export function useHealthService() {
    return useSystemServices().health;
}
/**
 * Hook to access ServicesService
 */
export function useServicesService() {
    return useSystemServices().services;
}
/**
 * Hook to access RabbitMQManagementService
 */
export function useRabbitMQManagementService() {
    return useSystemServices().rabbitMQManagement;
}
/**
 * Hook to access RedisManagementService
 */
export function useRedisManagementService() {
    return useSystemServices().redisManagement;
}
/**
 * Hook to access RabbitMQTestService
 */
export function useRabbitMQTestService() {
    return useSystemServices().rabbitMQTest;
}
/**
 * Hook to access RedisTestService
 */
export function useRedisTestService() {
    return useSystemServices().redisTest;
}
/**
 * Hook to access VendorSystemSettingsService
 */
export function useVendorSystemSettingsService() {
    return useSystemServices().vendorSystemSettings;
}
/**
 * Hook to access SystemRevenueService
 */
export function useSystemRevenueService() {
    return useSystemServices().systemRevenue;
}
/**
 * Hook to access PublicHolidayService
 */
export function usePublicHolidayService() {
    return useSystemServices().publicHoliday;
}
/**
 * Hook to access CustomFieldTypeService (shared custom-field-type catalogue)
 */
export function useCustomFieldTypeService() {
    return useSystemServices().customFieldType;
}
/**
 * Hook to access LogRetentionService (central log retention settings, admin only)
 */
export function useLogRetentionService() {
    return useSystemServices().logRetention;
}
/**
 * Hook to access HelpService (in-app help, tours, tour progress, help settings per app and Ask AI; admin help content)
 */
export function useHelpService() {
    return useSystemServices().help;
}
//# sourceMappingURL=useSystemServices.js.map